import { LOGGER_LOG_TYPE } from 'Config';
import Button from 'common/components/button/Button';
import InputError from 'common/components/inputError/InputError';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import { TextInputController } from 'common/components/textInput/TextInput';
import { Label } from 'common/components/texts/Texts';
import Loading from 'common/services/Loading';
import Toast from 'common/services/Toast';
import { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaTrashAlt } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import Logger from 'common/services/Logger';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { WorkTypeDto } from 'api/workTypes/models/WorkTypeDto';
import WorkTypesService from 'api/workTypes/WorkTypesService';
import { Form } from 'common/components/form/Form';
import YesNoModal from 'common/components/modal/yesNoModal/YesNoModal';

function WorkTypeScreen(): JSX.Element | null {
    const { id, type } = useParams<{ id: string, type: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const form = useForm<WorkTypeDto>({ shouldUnregister: false });
    const errors = form.formState.errors;
    const isDetails = type === 'details';
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [showConfirmDeleteRelationsModal, setShowConfirmDeleteRelationsModal] = useState(false);

    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['WORK_TYPES_WRITE'])
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['WORK_TYPES_READ'])

    const url = '/library/work-types';
    const currentUrl = url + '/' + type + (id ? ('/' + id) : '');

    useEffect(() => {
        void getData()
    }, [id, type]);

    const getData = async () => {
        try {
            let result: Partial<WorkTypeDto> = { id, name: '', budgetAbbreviation: '', tsAbbreviation: '' };
            if (id) {
                Loading.show();
                result = await WorkTypesService.getById(id);
            }
            form.reset(result);
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get work type information', error);
            Toast.error(t('messages.error_load_info'));
        }
    };

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`/library/work-types/${typeUrl}/${id}`);
        } else {
            navigate('/library/work-types');
        }
    }

    const onSubmit = async (model: WorkTypeDto) => {
        try {
            Loading.show();

            const sanitized: WorkTypeDto = {
                ...model,
                name: model.name?.trim() ?? '',
                budgetAbbreviation: model.budgetAbbreviation?.trim() ?? '',
                tsAbbreviation: model.tsAbbreviation?.trim() ?? '',
            };

            const trimmedName = sanitized.name;
            if (trimmedName) {
                const dupResult = await WorkTypesService.getList({
                    name: trimmedName,
                    page: 1,
                    itemsPerPage: 50,
                    orderColumn: 'name',
                    orderBy: 'asc'
                });
                const duplicate = dupResult.items?.find(w =>
                    w.name?.trim().toLowerCase() === trimmedName.toLowerCase() &&
                    w.id !== model.id
                );
                if (duplicate) {
                    const msg = t('work_types.name_already_exists');
                    Toast.warning(msg);
                    form.setError('name', { type: 'manual', message: msg });
                    Loading.hide();
                    return;
                }
            }

            if (sanitized.id) {
                await WorkTypesService.update(sanitized)
                // navigateTo('details', id);
                void getData();
            } else {
                const id = await WorkTypesService.create(sanitized)
                navigateTo('edit', id);
            }
            Loading.hide();
            Toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the work type with id: ${id}`, error);
            Toast.error(t('messages.record_save_error'));

            Loading.hide();
        }
    };

    const onInvalid = () => {
        Toast.warning(t('messages.required_fields_empty'));
    }

    const onDelete = async (result: boolean) => {
        if (!showConfirmDeleteModal) {
            return;
        }

        setShowConfirmDeleteModal(false);

        if (result) {
            setShowConfirmDeleteRelationsModal(true);
        }
    }

    const onDeleteRelations = async (result: boolean) => {
        Loading.show();
        try {
            await WorkTypesService.delete(id!, result)
            Toast.success(t('messages.record_delete_success'));
            navigateTo();
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete work type', error);
            Toast.error(t('messages.record_delete_error'));
        } finally {
            Loading.hide();
            setShowConfirmDeleteRelationsModal(false)
        }
    }

    const entityName = form.watch('name');
    const titlePrefix = type === 'create'
        ? t('common.new_title.work_type')
        : type === 'edit'
            ? t('common.edit_title.work_type')
            : t('common.details_title.work_type');
    const entityLabel = type !== 'create' && entityName ? entityName : undefined;
    const title = entityLabel ? `${titlePrefix} ${entityLabel}` : titlePrefix;

    if (!canRead) {
        return null;
    }

    return (
        <ScreenTitle title={t('work_types.title')}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: t('work_types.title'), url },
                    { name: title, url: currentUrl },
                ]}
            />
            <Form handleSubmit={form.handleSubmit} onSubmit={onSubmit} onInvalid={onInvalid} noValidate>
                <PageHeader title={titlePrefix} subTitle={entityLabel} onGoBack={() => navigateTo()} informationText={t('common.go_back')}>
                    {canWrite && !isDetails && <Button type='submit'>{t('common.save')}</Button>}
                    {canWrite && isDetails && <Button fw href={`/library/work-types/edit/${id}`}>{t('common.edit')}</Button>}
                    {canWrite && isDetails && (
                        <Button variant='danger' onClick={() => setShowConfirmDeleteModal(true)}>
                            <FaTrashAlt />
                        </Button>
                    )}
                </PageHeader>
                <PageContainer>
                    <Row className="mb-3">
                        <Col>
                            <Label space>{t('work_types.list.name')}{!isDetails ? '*' : ''}</Label>
                            <TextInputController
                                name='name' control={form.control}
                                placeholder={t('work_types.list.name')} disabled={isDetails}
                                rules={{ required: true, maxLength: 250 }}
                                hasError={Boolean(errors.name)} />
                            <InputError error={errors.name} maxLength={250} />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6}>
                            <Label space>{t('work_types.list.budget_abbreviation')}{!isDetails ? '*' : ''}</Label>
                            <TextInputController
                                name='budgetAbbreviation'
                                control={form.control}
                                placeholder={t('work_types.list.budget_abbreviation')}
                                disabled={isDetails}
                                rules={{ required: true, maxLength: 5 }}
                                hasError={Boolean(errors.budgetAbbreviation)}
                            />
                            <InputError error={errors.budgetAbbreviation} maxLength={5} />
                        </Col>
                        <Col xs={6}>
                            <Label space>{t('work_types.list.ts_abbreviation')}</Label>
                            <TextInputController
                                name='tsAbbreviation'
                                control={form.control}
                                placeholder={t('work_types.list.ts_abbreviation')}
                                disabled={isDetails}
                                rules={{ maxLength: 5 }}
                                hasError={Boolean(errors.tsAbbreviation)}
                            />
                            <InputError error={errors.tsAbbreviation} maxLength={5} />
                        </Col>
                    </Row>
                </PageContainer>
            </Form>

            <ConfirmDeleteModal
                itemName={form.getValues('name')}
                isOpen={showConfirmDeleteModal}
                onClose={onDelete}
            />

            <YesNoModal
                title={t('common.question')}
                message={t('work_types.delete_relations')}
                onClose={onDeleteRelations}
                isOpen={showConfirmDeleteRelationsModal}
            />
        </ScreenTitle>
    )
}

export default WorkTypeScreen;
